import Keycloak from 'keycloak-js';
import { create } from 'zustand';
import { config } from '../config.js';
import { checkTauriPresentSync } from '../services/backend/isDesktop.js';

const keycloakConfig = {
  url: config.keycloakUrl,
  realm: config.keycloakRealm,
  clientId: config.keycloakClientId,
};

// Global singleton to prevent multiple instances
let keycloakInstance = null;
let isInitializing = false;

export const useAuthStore = create((set, get) => ({
  keycloak: null,
  isAuthenticated: false,
  isLoading: true,
  user: null,
  token: null,
  isInitialized: false,

  initializeAuth: async () => {
    const state = get();

    // Prevent multiple initializations (React.StrictMode protection)
    if (state.isInitialized || isInitializing) {
      return;
    }

    isInitializing = true;

    // Desktop mode: skip Keycloak entirely (desktop backend has no auth).
    // Must match app.dl9_identity.DL9_USER on the backend — the project
    // ownership check in ProjectExplorer compares preferred_username to
    // project.creator_username, which the backend stamps as 'dl9-local'.
    if (checkTauriPresentSync()) {
      set({
        keycloak: null,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        user: {
          name: 'DatLab 9 Local',
          preferred_username: 'dl9-local',
          email: 'dl9-local@oroboros.at',
        },
        token: null,
      });
      isInitializing = false;
      return;
    }

    try {
      if (!keycloakInstance) {
        keycloakInstance = new Keycloak(keycloakConfig);
      }

      const authenticated = await keycloakInstance.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        pkceMethod: 'S256',
        enableLogging: true,
      });

      set({
        keycloak: keycloakInstance,
        isAuthenticated: authenticated,
        isLoading: false,
        isInitialized: true,
        user: authenticated ? keycloakInstance.tokenParsed : null,
        token: authenticated ? keycloakInstance.token : null,
      });

      // Set up token refresh
      if (authenticated && !keycloakInstance._tokenRefreshInterval) {
        keycloakInstance._tokenRefreshInterval = setInterval(() => {
          keycloakInstance
            .updateToken(70)
            .then((refreshed) => {
              if (refreshed) {
                set({
                  token: keycloakInstance.token,
                  user: keycloakInstance.tokenParsed,
                });
              }
            })
            .catch(() => {
              console.error('Failed to refresh token');
              get().logout();
            });
        }, 60000);
      }
    } catch (error) {
      console.error('Failed to initialize Keycloak:', error);
      set({
        isLoading: false,
        isAuthenticated: false,
        isInitialized: true,
      });
    } finally {
      isInitializing = false;
    }
  },

  login: () => {
    const { keycloak } = get();
    if (keycloak) {
      keycloak.login();
    }
  },

  logout: () => {
    const { keycloak } = get();
    if (keycloak) {
      // Clear token refresh interval
      if (keycloak._tokenRefreshInterval) {
        clearInterval(keycloak._tokenRefreshInterval);
        keycloak._tokenRefreshInterval = null;
      }

      set({
        isAuthenticated: false,
        user: null,
        token: null,
      });
      const redirectUri = `${window.location.origin}/`;
      keycloak.logout({ redirectUri });
    }
  },

  getToken: () => {
    return get().token;
  },
}));
